import { fetchAndActivate, getBoolean, getString } from 'firebase/remote-config';
import { isFirebaseConfigured, remoteConfig } from '../firebase';
import { z } from 'zod';

const booleanCoerce = (defaultVal: boolean) =>
  z.preprocess((val) => {
    if (val === undefined || val === null || val === '') return defaultVal;
    if (typeof val === 'string') {
      const lower = val.toLowerCase().trim();
      if (lower === 'true' || lower === '1') return true;
      if (lower === 'false' || lower === '0') return false;
    }
    return Boolean(val);
  }, z.boolean().default(defaultVal));

export const RemoteConfigSchema = z
  .object({
    enable_payment: booleanCoerce(true),
    require_subscription: booleanCoerce(true),
    enable_signup: booleanCoerce(true),
    enable_new_pallete: booleanCoerce(false),
    support_email: z.string().default('kontakt@centrumcoucha.pl'),
    login_url: z.string().default(''),
    max_file_size_mb: z.coerce.number().default(5),
    enable_languages: booleanCoerce(true),
    client_password_min_length: z.coerce.number().default(10),
    client_password_min_uppercase: z.coerce.number().default(1),
    client_password_min_lowercase: z.coerce.number().default(1),
    client_password_min_numbers: z.coerce.number().default(1),
    client_password_min_special_chars: z.coerce.number().default(1),
  })
  .loose();

export type RemoteConfigValues = z.infer<typeof RemoteConfigSchema>;

class RemoteConfigService {
  private values: RemoteConfigValues = {
    enable_payment: true,
    require_subscription: true,
    enable_signup: true,
    enable_new_pallete: import.meta.env.VITE_ENABLE_NEW_PALETTE === 'true',
    support_email: 'kontakt@centrumcoucha.pl',
    login_url: '',
    max_file_size_mb: 5,
    enable_languages: true,
    client_password_min_length: 10,
    client_password_min_uppercase: 1,
    client_password_min_lowercase: 1,
    client_password_min_numbers: 1,
    client_password_min_special_chars: 1,
  };
  private initialized = false;
  private initPromise: Promise<RemoteConfigValues> | null = null;

  async init(): Promise<RemoteConfigValues> {
    if (this.initialized) return this.values;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      if (isFirebaseConfigured && remoteConfig) {
        try {
          // Pobieramy i aktywujemy konfigurację raz przy starcie
          await fetchAndActivate(remoteConfig);
          const rawConfig = {
            enable_payment: getBoolean(remoteConfig, 'enable_payment'),
            require_subscription: getBoolean(remoteConfig, 'require_subscription'),
            enable_signup: getBoolean(remoteConfig, 'enable_signup'),
            enable_new_pallete: getBoolean(remoteConfig, 'enable_new_pallete'),
            support_email: getString(remoteConfig, 'support_email') || 'kontakt@centrumcoucha.pl',
            login_url: getString(remoteConfig, 'login_url') || '',
            max_file_size_mb: getString(remoteConfig, 'max_file_size_mb') || 5,
            enable_languages: getBoolean(remoteConfig, 'enable_languages'),
            client_password_min_length: getString(remoteConfig, 'client_password_min_length') || 10,
            client_password_min_uppercase: getString(remoteConfig, 'client_password_min_uppercase') || 1,
            client_password_min_lowercase: getString(remoteConfig, 'client_password_min_lowercase') || 1,
            client_password_min_numbers: getString(remoteConfig, 'client_password_min_numbers') || 1,
            client_password_min_special_chars: getString(remoteConfig, 'client_password_min_special_chars') || 1,
          };

          const parsed = RemoteConfigSchema.safeParse(rawConfig);
          if (parsed.success) {
            this.values = parsed.data;
          } else {
            console.warn('[RemoteConfigService] Błąd walidacji Remote Config:', parsed.error.issues);
          }
          
          const isDebug = import.meta.env.DEV || (typeof window !== 'undefined' && (window.location.hostname.includes('stg') || window.location.hostname.includes('staging') || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));
          if (isDebug) {
            console.log("📡 [RemoteConfigService] Załadowano konfigurację zdalną:", this.values);
          }
        } catch (error) {
          console.error("❌ [RemoteConfigService] Błąd pobierania konfiguracji zdalnej:", error);
        }
      } else {
        const isDebug = import.meta.env.DEV || (typeof window !== 'undefined' && (window.location.hostname.includes('stg') || window.location.hostname.includes('staging') || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));
        if (isDebug) {
          console.log("📡 [RemoteConfigService] Brak połączenia z Firebase (tryb DEMO). Używam domyślnych wartości:", this.values);
        }
      }
      this.initialized = true;
      return this.values;
    })();

    return this.initPromise;
  }

  get enablePayment(): boolean {
    return this.values.enable_payment;
  }

  get requireSubscription(): boolean {
    return this.values.require_subscription;
  }

  get enableSignup(): boolean {
    return this.values.enable_signup;
  }

  get enableNewPalette(): boolean {
    return this.values.enable_new_pallete;
  }

  get supportEmail(): string {
    return this.values.support_email;
  }

  get loginUrl(): string {
    return this.values.login_url;
  }

  get maxFileSizeMb(): number {
    return this.values.max_file_size_mb;
  }

  get enableLanguages(): boolean {
    return this.values.enable_languages;
  }

  get clientPasswordMinLength(): number {
    return this.values.client_password_min_length;
  }

  get clientPasswordMinUppercase(): number {
    return this.values.client_password_min_uppercase;
  }

  get clientPasswordMinLowercase(): number {
    return this.values.client_password_min_lowercase;
  }

  get clientPasswordMinNumbers(): number {
    return this.values.client_password_min_numbers;
  }

  get clientPasswordMinSpecialChars(): number {
    return this.values.client_password_min_special_chars;
  }
}

export const remoteConfigService = new RemoteConfigService();
export default remoteConfigService;
